import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  ASSET_KIND_FILTER_LABELS,
  ASSET_KINDS,
  METER_TYPE_LABELS,
  METER_TYPES,
  NLSFB,
  PART_FILTER_LABELS,
  PART_FILTERS,
  assetTypeLabel,
  nlsfbGroupCode,
  nlsfbLabel,
  nlsfbMainCode,
  type AssetDto,
  type AssetKind,
  type BuildingDto,
  type LabelLayout,
  type MeterDto,
  type PartFilter,
} from "@klantpunt/shared";
import {
  IconClose,
  IconMore,
  IconParts,
  IconPencil,
  IconPin,
  IconPlus,
  IconScan,
  IconSelect,
  IconTag,
  IconUpload,
} from "../../app/icons";
import { ToolPopover } from "../../app/ToolPopover";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { AssetThumb } from "./assetIcons";
import { buildLabelsPdf, downloadPdf } from "./labels";
import { LabelPrintDialog } from "./LabelPrintDialog";
import { Segmented } from "../../app/Choice";
import { AssetlogImportDialog, readExcelFile } from "./AssetlogImport";
import { MetrelImportDialog } from "./MetrelImport";
import { BulkDialog, type BulkMode } from "./BulkActions";

/**
 * Welke lijst: assets (installaties en bouwkundig, zonder module Meterstanden ook meters), arbeidsmiddelen
 * (ladders, gereedschap, apparaten) of meters.
 */
export type AssetListKind = "assets" | "equipment" | "meters";
const LIST_TITLE: Record<AssetListKind, string> = { assets: "Assets", equipment: "Arbeidsmiddelen", meters: "Meters" };
const NEW_LABEL: Record<AssetListKind, string> = { assets: "Asset", equipment: "Arbeidsmiddel", meters: "Meter" };

const inList = (list: AssetListKind, meterList: boolean, k: AssetKind) =>
  list === "equipment"
    ? k === "equipment"
    : list === "meters"
      ? k === "meter"
      : k !== "equipment" && (!meterList || k !== "meter");

/** Assets van een gebouw, gegroepeerd per NL-SfB-hoofdgroep; zoeken en filteren op elementgroep. */
export function AssetList({
  building: b,
  floorId,
  roomId = null,
  selected,
  onOpen,
  onNew,
  onPlaceMany,
  list: listKind = "assets",
  children,
}: {
  building: BuildingDto;
  list?: AssetListKind;
  /** Alleen assets op deze verdieping (null: heel gebouw). */
  floorId: string | null;
  /** Alleen assets in deze ruimte. */
  roomId?: string | null;
  selected: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
  /** Bulkactie: de gekozen assets één voor één op de tekening plaatsen (in de volgorde van de lijst). */
  onPlaceMany?: (assets: AssetDto[]) => void;
  /** Boven de lijst, onder de kop (bijvoorbeeld de lopende meterronde). */
  children?: ReactNode;
}) {
  const { data: me } = useMe();
  const toast = useToast();
  const cache = useBuildingCache(b.id);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [kind, setKind] = useState<AssetKind | "">("");
  const [partFilter, setPartFilter] = useState<PartFilter>("all");
  const [busy, setBusy] = useState(false);
  const [assetlog, setAssetlog] = useState<{ file: string; name: string } | null>(null);
  const [metrel, setMetrel] = useState<{ file: string; name: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const metrelRef = useRef<HTMLInputElement>(null);
  const canManage = can(me, "asset.manage");
  const floorName = (id: string | null) => b.floors.find((f) => f.id === id)?.name;
  const roomName = (id: string | null) => b.rooms.find((r) => r.id === id)?.name;
  const equipment = listKind === "equipment";
  const meterList = hasModule(me, "meters");
  const ofList = (k: AssetKind) => inList(listKind, meterList, k);

  const kinds: readonly AssetKind[] = ASSET_KINDS.filter(ofList);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return b.assets.filter(
      (a) =>
        inList(listKind, meterList, a.assetKind) &&
        (!floorId || a.floorId === floorId) &&
        (!roomId || a.roomId === roomId) &&
        (!kind || a.assetKind === kind) &&
        (partFilter === "all" || (partFilter === "parts") === Boolean(a.parentAssetId)) &&
        (!cat ||
          (cat === "none"
            ? !a.elementCode
            : cat.length === 1
              ? nlsfbMainCode(a.elementCode ?? "") === cat
              : nlsfbGroupCode(a.elementCode ?? "") === cat)) &&
        (!t ||
          [a.code, a.name, a.brand, a.model, a.serialNumber, nlsfbLabel(a.elementCode), assetTypeLabel(a.assetType)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(t)),
    );
  }, [b.assets, floorId, roomId, q, cat, kind, partFilter, listKind, meterList]);
  const hasParts = b.assets.some((a) => a.parentAssetId && inList(listKind, meterList, a.assetKind));
  const used = new Set(b.assets.map((a) => nlsfbGroupCode(a.elementCode ?? "")));
  const isMeters = listKind === "meters";
  // Meters groeperen per soort, met de tussenmeters direct onder hun hoofdmeter (NL-SfB zegt bij meters niets).
  const meterQ = useQuery({
    queryKey: buildingMetersKey(b.id),
    queryFn: () => api<MeterDto[]>(`/buildings/${b.id}/meters`),
    enabled: isMeters,
  });
  const meterOf = new Map((meterQ.data ?? []).map((m) => [m.assetId, m]));
  // Onderdelen (docs/ASSETS-MJOP.md, Onderdelen) direct onder hun hoofdasset, in de groep van de hoofdasset.
  // Valt de hoofdasset buiten het filter, dan staat het onderdeel los met "Onderdeel van …".
  const shown = new Set(list.map((a) => a.id));
  const nested = (a: AssetDto) => !!a.parentAssetId && shown.has(a.parentAssetId);
  const mains = list.filter((a) => !nested(a));
  const withParts = (items: AssetDto[]) => items.flatMap((m) => [m, ...list.filter((c) => c.parentAssetId === m.id)]);
  const partCount = (id: string) => b.assets.filter((a) => a.parentAssetId === id).length;
  const groups = isMeters
    ? [
        ...METER_TYPES.map((t) => {
          const ofType = list.filter((a) => meterOf.get(a.id)?.meterType === t);
          const inType = new Set(ofType.map((a) => a.id));
          const isMain = (a: AssetDto) => !inType.has(meterOf.get(a.id)?.parentAssetId ?? "");
          return {
            code: t,
            label: METER_TYPE_LABELS[t],
            items: ofType
              .filter(isMain)
              .flatMap((main) => [main, ...ofType.filter((c) => meterOf.get(c.id)?.parentAssetId === main.id)]),
          };
        }),
        { code: "none", label: "Nog niet ingesteld", items: list.filter((a) => !meterOf.has(a.id)) },
      ].filter((g) => g.items.length && (meterQ.data || g.code === "none"))
    : [
        ...NLSFB.map((m) => ({
          code: m.code,
          label: `${m.code}- ${m.label}`,
          items: withParts(mains.filter((a) => a.elementCode && nlsfbMainCode(a.elementCode) === m.code)),
        })),
        { code: "none", label: "Niet ingedeeld", items: withParts(mains.filter((a) => !a.elementCode)) },
      ].filter((g) => g.items.length);
  const assetCode = (id: string) => b.assets.find((a) => a.id === id)?.code ?? "";
  /** Meters die door de gekozen plek buiten de lijst vallen. */
  const elsewhere =
    isMeters && (floorId || roomId)
      ? b.assets.filter((a) => a.assetKind === "meter").length -
        b.assets.filter(
          (a) => a.assetKind === "meter" && (!floorId || a.floorId === floorId) && (!roomId || a.roomId === roomId),
        ).length
      : 0;

  async function pickAssetlog(f: File | undefined) {
    if (!f) return;
    try {
      setAssetlog({ file: await readExcelFile(f), name: f.name });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Dit bestand kan niet worden gelezen");
    }
  }

  async function pickMetrel(f: File | undefined) {
    if (!f) return;
    try {
      setMetrel({ file: await readExcelFile(f), name: f.name });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Dit bestand kan niet worden gelezen");
    }
  }

  // Bulkacties: meerdere assets kiezen en één actie op allemaal toepassen.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [bulk, setBulk] = useState<BulkMode | null>(null);
  const order = groups.flatMap((g) => g.items);
  const pickedAssets = order.filter((a) => picked.has(a.id));
  const allPicked = order.length > 0 && order.every((a) => picked.has(a.id));
  const toggle = (id: string) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setPicked(new Set());
  };

  const [labels, setLabels] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // QR-labels standaard voor installaties en meters; andere soorten alleen als dat filter gekozen is (§5.5).
  const labelAssets = list.filter((a) =>
    kind || listKind !== "assets"
      ? a.assetKind === (kind || (equipment ? "equipment" : "meter"))
      : a.assetKind === "installation" || a.assetKind === "meter",
  );
  const labelIds = labelAssets.map((a) => a.id);
  // Assets zonder QR-code krijgen er een; een gekoppelde sticker (bijv. Assetlog) houdt zijn code.
  const labelCount = labelAssets.length;

  async function printLabels(layout: LabelLayout, skip: number) {
    setBusy(true);
    try {
      const ids = labelIds;
      const all = await api<AssetDto[]>(`/buildings/${b.id}/assets/qr`, { method: "POST", body: { ids } });
      all.forEach((a) => cache.upsertAsset(a));
      const { bytes, count } = await buildLabelsPdf(
        all.filter((a) => ids.includes(a.id)),
        b.name,
        layout,
        { skip },
      );
      downloadPdf(bytes, `QR-labels ${b.code}.pdf`);
      toast(`${count} QR-label${count === 1 ? "" : "s"} gemaakt`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Labels maken mislukt");
      throw e;
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="tablewrap assetlist" aria-label={LIST_TITLE[listKind]}>
      <div className="tablehead">
        <div className="grow">
          <p className="label">
            {b.name}
            {floorId && floorName(floorId) ? ` · ${floorName(floorId)}` : ""}
            {roomId && roomName(roomId) ? ` › ${roomName(roomId)}` : ""}
          </p>
          <h1>
            {LIST_TITLE[listKind]} <span className="muted num">{list.length}</span>
          </h1>
        </div>
        {canManage && (
          <span className="pageactions">
            {list.length > 0 && (
              <button
                type="button"
                className="btn ghost"
                aria-pressed={selecting}
                onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
              >
                <IconSelect size={18} aria-hidden="true" />
                {selecting ? "Klaar" : "Selecteren"}
              </button>
            )}
            <button type="button" className="btn primary" onClick={onNew}>
              <IconPlus size={18} aria-hidden="true" />
              {NEW_LABEL[listKind]}
            </button>
            <ToolPopover
              label="Meer acties voor assets"
              title="Meer acties voor assets"
              icon={<IconMore size={20} aria-hidden="true" />}
              hideLabel
              open={menuOpen}
              onOpenChange={setMenuOpen}
            >
              <div className="pagemenu">
                {list.length > 0 && (
                  <button
                    type="button"
                    className="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      if (labelCount) setLabels(true);
                      else
                        toast(
                          "Geen installaties in deze lijst. Kies het filter Bouwkundig voor labels van bouwkundige assets.",
                        );
                    }}
                    disabled={busy}
                  >
                    <IconScan size={20} aria-hidden="true" />
                    QR-labels printen
                  </button>
                )}
                {listKind === "assets" && (
                  <button
                    type="button"
                    className="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      fileRef.current?.click();
                    }}
                  >
                    <IconUpload size={20} aria-hidden="true" />
                    Inlezen Assetlog-Excel
                  </button>
                )}
                {equipment && (
                  <button
                    type="button"
                    className="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      metrelRef.current?.click();
                    }}
                  >
                    <IconUpload size={20} aria-hidden="true" />
                    Inlezen Metrel-keuringen
                  </button>
                )}
              </div>
            </ToolPopover>
          </span>
        )}
        {canManage && (
          <>
            {/* Buiten het menu, zodat de keuze na het sluiten van het menu nog binnenkomt. */}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="sr-only"
              tabIndex={-1}
              aria-label="Assetlog-Excel kiezen"
              data-testid="assetlog-file"
              onChange={(e) => {
                void pickAssetlog(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <input
              ref={metrelRef}
              type="file"
              accept=".padfx"
              className="sr-only"
              tabIndex={-1}
              aria-label="Metrel-project (.padfx) kiezen"
              data-testid="metrel-file"
              onChange={(e) => {
                void pickMetrel(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </>
        )}
      </div>
      <div className="tablefilters">
        <input
          type="search"
          placeholder="Zoek op code, naam of merk"
          aria-label="Assets zoeken"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {kinds.length > 1 && (
          <Segmented
            label="Soort asset"
            items={(["", ...kinds] as const).map((k) => ({
              value: (k || "all") as AssetKind | "all",
              label: k ? ASSET_KIND_FILTER_LABELS[k] : "Alle",
            }))}
            value={kind || "all"}
            onChange={(k) => setKind(k === "all" ? "" : k)}
          />
        )}
        {hasParts && (
          <select
            aria-label="Onderdelen"
            value={partFilter}
            onChange={(e) => setPartFilter(e.target.value as PartFilter)}
          >
            {PART_FILTERS.map((f) => (
              <option key={f} value={f}>
                {PART_FILTER_LABELS[f]}
              </option>
            ))}
          </select>
        )}
        {!isMeters && (
          <select aria-label="Element" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">Alle elementen</option>
            <option value="none">Niet ingedeeld</option>
            {NLSFB.map((m) => (
              <optgroup key={m.code} label={`${m.code}- ${m.label}`}>
                <option value={m.code}>Alle {m.label.toLowerCase()}</option>
                {m.groups
                  .filter((g) => used.has(g.code))
                  .map((g) => (
                    <option key={g.code} value={g.code}>
                      {g.code} {g.label}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        )}
      </div>
      {children}
      <div className="tscroll">
        {groups.map((g) => (
          <div key={g.code} className="assetgroup">
            <p className="label">
              {g.label} <span className="num">{g.items.length}</span>
            </p>
            <ul className="assetrows">
              {g.items.map((a) => {
                const parent = isMeters ? meterOf.get(a.id)?.parentAssetId : null;
                const indent = isMeters ? !!parent && g.code !== "none" : nested(a);
                const parts = isMeters ? 0 : partCount(a.id);
                const body = (
                  <>
                    <AssetThumb asset={a} size={44} />
                    <span className="grow">
                      <span className="code small">{a.code}</span> <strong>{a.name}</strong>
                      <span className="muted small block">
                        {isMeters
                          ? parent
                            ? `Tussenmeter van ${assetCode(parent)}`
                            : !meterOf.has(a.id)
                              ? "Stel de meter in via Opnames"
                              : (meterQ.data ?? []).some((c) => c.parentAssetId === a.id)
                                ? "Hoofdmeter"
                                : [meterOf.get(a.id)!.unit, meterOf.get(a.id)!.ean && `EAN ${meterOf.get(a.id)!.ean}`]
                                    .filter(Boolean)
                                    .join(" · ")
                          : a.parentAssetId && !indent
                            ? `Onderdeel van ${assetCode(a.parentAssetId)}`
                            : assetTypeLabel(a.assetType)
                              ? `${assetTypeLabel(a.assetType)}${a.elementCode ? ` · ${a.elementCode}` : ""}`
                              : nlsfbLabel(a.elementCode)}
                        {[a.brand, a.model].filter(Boolean).length
                          ? ` · ${[a.brand, a.model].filter(Boolean).join(" ")}`
                          : ""}
                        {floorName(a.floorId) ? ` · ${floorName(a.floorId)}` : ""}
                        {roomName(a.roomId) ? ` › ${roomName(a.roomId)}` : ""}
                      </span>
                    </span>
                    {parts > 0 && (
                      <span className="chip">
                        {parts} {parts === 1 ? "onderdeel" : "onderdelen"}
                      </span>
                    )}
                    {a.qrCode && <span className="chip">QR</span>}
                    {a.fileCount > 0 && <span className="chip">{a.fileCount} bijl.</span>}
                  </>
                );
                return (
                  <li key={a.id} className={indent ? "submeter" : undefined}>
                    {selecting ? (
                      <label className="assetrow picking">
                        <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
                        {body}
                      </label>
                    ) : (
                      <button
                        type="button"
                        className="assetrow"
                        aria-current={a.id === selected}
                        onClick={() => onOpen(a.id)}
                      >
                        {body}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {elsewhere > 0 && (
          <p className="muted small">
            Nog {elsewhere} {elsewhere === 1 ? "meter" : "meters"} op andere verdiepingen. Kies{" "}
            <em>Alle verdiepingen</em> om ze te zien.
          </p>
        )}
        {groups.length === 0 && (
          <p className="emptylist">
            {!b.assets.some((a) => ofList(a.assetKind))
              ? listKind === "meters"
                ? "Nog geen meters. Voeg er een toe, of tik op de tekening."
                : listKind === "equipment"
                  ? "Nog geen arbeidsmiddelen."
                  : "Nog geen assets. Voeg er een toe, of tik op de tekening."
              : roomId
                ? "Niets gevonden in deze ruimte."
                : floorId && !b.assets.some((a) => ofList(a.assetKind) && a.floorId === floorId)
                  ? "Niets gevonden op deze verdieping."
                  : "Niets gevonden met deze filters."}
          </p>
        )}
      </div>
      {selecting && (
        <div className="bulkbar" role="region" aria-label="Bulkacties">
          <label className="check">
            <input
              type="checkbox"
              checked={allPicked}
              onChange={() => setPicked(allPicked ? new Set() : new Set(order.map((a) => a.id)))}
            />
            <span>
              <strong className="num">{pickedAssets.length}</strong> van {order.length} gekozen
            </span>
          </label>
          <button
            type="button"
            className="btn small ghost bulkclose"
            onClick={stopSelecting}
            aria-label="Selectie sluiten"
          >
            <IconClose size={16} aria-hidden="true" />
          </button>
          <span className="acts">
            <button type="button" className="btn small" disabled={!pickedAssets.length} onClick={() => setBulk("type")}>
              <IconTag size={16} aria-hidden="true" />
              Type aanpassen
            </button>
            <button type="button" className="btn small" disabled={!pickedAssets.length} onClick={() => setBulk("data")}>
              <IconPencil size={16} aria-hidden="true" />
              Gegevens aanvullen
            </button>
            {onPlaceMany && b.floors.some((f) => f.plan) && (
              <button
                type="button"
                className="btn small"
                disabled={!pickedAssets.length}
                onClick={() => {
                  onPlaceMany(pickedAssets);
                  stopSelecting();
                }}
              >
                <IconPin size={16} aria-hidden="true" />
                Op tekening plaatsen
              </button>
            )}
            <button
              type="button"
              className="btn small"
              disabled={!pickedAssets.length}
              onClick={() => setBulk("parent")}
            >
              <IconParts size={16} aria-hidden="true" />
              Onder hoofdasset
            </button>
          </span>
        </div>
      )}
      {bulk && pickedAssets.length > 0 && (
        <BulkDialog building={b} mode={bulk} assets={pickedAssets} onClose={() => setBulk(null)} />
      )}
      {labels && <LabelPrintDialog count={labelCount} onClose={() => setLabels(false)} onPrint={printLabels} />}
      {metrel && (
        <MetrelImportDialog
          building={b}
          file={metrel.file}
          name={metrel.name}
          full={me?.org?.elementCoding ?? false}
          onClose={() => setMetrel(null)}
        />
      )}
      {assetlog && (
        <AssetlogImportDialog
          building={b}
          file={assetlog.file}
          name={assetlog.name}
          full={me?.org?.elementCoding ?? false}
          onClose={() => setAssetlog(null)}
        />
      )}
    </section>
  );
}

export const buildingMetersKey = (buildingId: string) => ["building-meters", buildingId] as const;
